// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O texto do mapa — lógica pura
// ============================================================================
//
// Tudo aqui é função pura dos dados. Nenhuma leitura de DOM, nenhum estado. São
// as regras de leitura do mapa, e dentro de um componente só se verificariam a
// olho.
//
// AS TRÊS BANDAS DE UMA LINHA
//   1 · barra do itinerário — cliente, escrito UMA vez por corrida
//   2 · localidade do dia   — muda todos os dias, por isso é por dia
//   3 · barra do motorista  — nome, escrito UMA vez por corrida
//
//   São as três sublinhas que as planeadoras conhecem. O que mudou é que duas
//   delas deixaram de repetir o mesmo texto em cada célula: repetir `IGT` oito
//   vezes seguidas é ruído, e era a principal razão de o mapa ser ilegível.
//
// O DIA DE ROTAÇÃO É A EXCEPÇÃO
//   Nesse dia as bandas não se aplicam: a célula mostra a **chegada** e a
//   **partida**, e não é escolha nossa — é o que os planeadores já fazem (126
//   células medidas). Ver `resumoRotacao`.
// ============================================================================

import {
  BANDEIRA,
  comMarcaDePresumida,
  fraseDaAlteracao,
  TITULO_DESTINO_PRESUMIDO,
  horaDaViagemAlheiaNaRotacao,
  servicoDeUmSoDia,
  partidaDeUmSoDia,
  minutosDaHora,
  type CelulaCompacta,
  type DadosMapa,
} from '@xlv-gva-mpt/dominio';
import { natureza } from './segmentos.js';
import { extentoDoServico } from './barras-do-subcontratado.js';
import { dataUtcDoIndice, janelaDe } from '@xlv-gva-mpt/dominio';

/**
 * Extrai `HH:MM` de um texto de movimento.
 *
 * O gerador escreve `in 19:25`; a folha real escreve também `BCN out 07:10`,
 * `MAD out ave 8:50`, `BCN barco 9:30`. O padrão comum é «uma hora em algum sítio
 * do texto», e é isso que se procura — não uma posição fixa, que quebraria no
 * primeiro formato diferente.
 *
 * Devolve `''` quando não há hora: um texto sem hora é informação legítima
 * (`Vazio`, `Volvo`), não um erro.
 */
export function horaDe(texto: string): string {
  const m = /(\d{1,2}):(\d{2})/.exec(texto);
  if (m === null) return '';
  return `${m[1]?.padStart(2, '0') ?? '00'}:${m[2] ?? '00'}`;
}

/**
 * Um par `[chegada, partida]` em texto legível — «chegada do grupo às 18:30».
 *
 * ⚠️ **Diz o SENTIDO que está registado e não o que se espera.** O par vem como
 * `['in 18:30', 'out 14:25']`, e cada posição pode estar vazia. Traduzir `in` para «chegada
 * do grupo» é fiel a F-25 — *«o autocarro vai buscar o grupo ao aeroporto»*, medido em
 * 1 241 primeiros dias contra 438 — e é a linguagem que o cliente usa: *«a hora de chegada
 * do grupo»* (2026-08-12).
 */
export function descreverHoras(par: readonly [string, string] | null): string {
  if (par === null) return '';
  const partes: string[] = [];
  const chegada = horaDe(par[0] ?? '');
  const partida = horaDe(par[1] ?? '');
  if (chegada !== '') partes.push(`chegada do grupo às ${chegada}`);
  if (partida !== '') partes.push(`saída do grupo às ${partida}`);
  return partes.join(' · ');
}

/**
 * A margem da rotação em minutos — **`in` menos `out`**. Requisito V4.
 *
 * ⚠️⚠️ **O SINAL ESTAVA INVERTIDO ATÉ 2026-09-07, e esta função é ANTERIOR a D-084.**
 * Devolvia `out − in` e nunca foi realinhada com a regra que o cliente fixou: o `out` é a
 * saída do grupo que **ACABA** e o `in` a chegada do grupo que **PARTE** (D-084, migração
 * 028), logo a margem é `in − out`. É a mesma conta de `avaliarHorasDaRotacao` em
 * `@xlv-gva-mpt/dominio`, e há prova a exigir que as duas concordem.
 *
 * 📗 A frase do cliente que a fixa (2026-08-14): *«se uma viagem tem out às 12 e in às 18
 * não tem problema ser o mesmo autocarro, mas se for ao contrário, o in às 12 e o out às 18
 * já temos problema, porque a viagem não pode esperar esse tempo.»*
 *
 * | sinal | o que é | leitura |
 * |---|---|---|
 * | **positivo** | FOLGA do autocarro | o grupo antigo já saiu quando o novo aterra |
 * | **negativo** | ESPERA do grupo novo | o autocarro ainda está com a viagem anterior |
 *
 * ⚠️ **O que o defeito produzia era o pior dos dois enganos:** o caso mau saía **positivo e
 * grande**, e um número positivo grande lê-se como folga confortável. Medido no `dev`
 * (viatura 297, 2026-08-29): `in 08:55` com `out 19:40` mostrava **«10h45 de rotação»**
 * quando são **10h45 de espera do grupo**, contra um máximo configurado de 4 h.
 *
 * ⚠️ **Nunca `Math.abs`.** Apagar o sinal é exactamente o defeito: os dois casos do cliente
 * têm o mesmo valor absoluto e respostas opostas.
 *
 * ⚠️ O caso medido da viatura 216 — chegada às 18:30 e partida às 13:10 do mesmo dia — é
 * **saudável** sob esta regra (+5h20 de folga), e não o «erro de registo» que este comentário
 * lhe chamava. São **23 %** das caixas pretas reais.
 */
export function tempoRotacaoMinutos(movimento: readonly [string, string] | null): number | null {
  if (movimento === null) return null;
  const chegada = horaDe(movimento[0]);
  const partida = horaDe(movimento[1]);
  if (chegada === '' || partida === '') return null;
  const min = (h: string): number => {
    const [hh, mm] = h.split(':');
    return Number(hh) * 60 + Number(mm);
  };
  // `chegada` é o `in` (grupo novo) e `partida` é o `out` (grupo antigo) — ver a tabela acima.
  return min(chegada) - min(partida);
}

/**
 * A margem do dia de rotação que o PAINEL mostra — cada hora lida da SUA linha.
 *
 * 📗 Cliente, 2026-09-29: *«Problema detectado no dia de rotação»* — uma viagem que acaba chega a
 * BCN às **18:45** (só `in`, sem `out`) e o motor propôs recolher lá um grupo às **10:40**.
 *
 * ⚠️⚠️ **O par do dia NÃO serve para isto num dia com duas linhas.** `construirDadosMapa` escreve
 *    as horas da `partida` **por cima** do par — de propósito, seis leitores mostram «as horas do
 *    dia» —, logo:
 *
 *      · a viagem que acaba só com `in 18:45` e a nossa com `in 10:40` davam o par
 *        `['in 10:40', '']` — «tempo não calculável», e as **8h05 de espera** ficavam escondidas;
 *      · uma `partida` com `in` **e** `out` (o grupo chega e sai logo, ~1 em 3 primeiros dias)
 *        punha o `out` DELA no lugar do da viagem que acaba, e a margem era medida contra a
 *        viagem errada.
 *
 * 📌 Por isso, com duas linhas, a viagem que acaba lê-se de `movimentosChegada` e a que parte de
 *    `movimentosSaida`, e **qual das duas horas de cada uma** é `horaDaViagemAlheiaNaRotacao` — a
 *    mesma escolha do motor, do arrasto e da escrita. Com uma linha só (a marca de rotação sem
 *    `partida` registada), fica a conta de sempre sobre o par.
 */
export function margemDaRotacaoDoDia(
  dados: DadosMapa,
  viatura: number,
  dia: number,
): number | null {
  const chave = `${viatura}:${dia}`;
  const daQueParte = dados.movimentosSaida[chave];
  if (daQueParte === undefined) return tempoRotacaoMinutos(dados.movimentos[viatura]?.[dia] ?? null);
  // ⚠️ Sem chave em `movimentosChegada` a viagem que acaba não tem hora nenhuma — e aí não se sabe.
  const daQueAcaba = dados.movimentosChegada[chave] ?? null;
  // ⚠️ E se cada viagem é de UM só dia: a que acaba lê-se das bandeiras da célula (são dela, a
  //    linha principal); a que parte, do dia seguinte (`partidaDeUmSoDia`).
  const celula = dados.grelha[viatura]?.[dia];
  const liberta = horaDaViagemAlheiaNaRotacao('partimos', {
    inMin: minutosDaHora(daQueAcaba?.[0] ?? null),
    outMin: minutosDaHora(daQueAcaba?.[1] ?? null),
    umSoDia: servicoDeUmSoDia(celula),
  });
  const precisa = horaDaViagemAlheiaNaRotacao('chegamos', {
    inMin: minutosDaHora(daQueParte[0]),
    outMin: minutosDaHora(daQueParte[1]),
    umSoDia: partidaDeUmSoDia(celula, dados.grelha[viatura]?.[dia + 1]),
  });
  return liberta === null || precisa === null ? null : precisa - liberta;
}

/** `2h00`, `45min`, `-5h20`. */
export function duracao(minutos: number): string {
  const s = minutos < 0 ? '-' : '';
  const m = Math.abs(minutos);
  return m < 60 ? `${s}${m}min` : `${s}${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}

/**
 * Meios que merecem ícone. Requisito V8: ferry e comboio, além do voo.
 *
 * ⚠️ **`comboio` e não `comboio_ave`.** A base de dados guarda a categoria — `comboio`
 * — e a informação que a lei pede num campo próprio: `com_cabine`, porque o art. 9.º
 * n.º 2 faz depender o repouso de haver cabine, não da marca do comboio.
 *
 * `comboio_ave` continua aceite porque é o que os CSV do gerador produzem, e a leitura
 * dos CSV é o caminho que serve o mapa quando não há base de dados. **Sem esta linha, a
 * mesma viagem tinha ícone de comboio numa origem e nenhum na outra** — e isso foi
 * apanhado a comparar as duas célula a célula, não a olhar para o ecrã.
 */
export type MeioComIcone = 'voo' | 'comboio' | 'ferry';

/**
 * Que ícone mostrar para um meio de deslocação, ou `null`.
 *
 * Devolve a **chave** e não o desenho: quem desenha é `icones.tsx`, com SVG de
 * cor única. A versão anterior devolvia emoji, que trazem cor própria para dentro
 * de um mapa onde a cor tem significado, e mudam de forma de sistema para sistema.
 *
 * `estrada` e desconhecidos não levam ícone: um ícone em quase todas as células
 * deixa de distinguir o que quer que seja. Medido: `estrada` são 16 598 das
 * 90 208 alocações.
 */
export function meioComIcone(meio: string): MeioComIcone | null {
  if (meio === 'voo' || meio === 'ferry') return meio;
  // As duas escritas do mesmo meio: a da base (`comboio`) e a dos CSV (`comboio_ave`).
  if (meio === 'comboio' || meio === 'comboio_ave') return 'comboio';
  return null;
}

/**
 * O nome do itinerário, com recurso ao código quando não tem nome.
 *
 * ⚠️ **Uma só função para os cinco sítios que o mostram.** `dados.itinerarios[i]` é um **par**
 * desde 2026-08-14, e um par renderizado em JSX sai como `IT-BCN5PARLoire Valley` — sem erro de
 * tipos, porque `ReactNode` aceita arrays. Foi por isso que passou a haver uma função.
 */
export function nomeOuCodigoDoItinerario(dados: DadosMapa, itinerario: number): string {
  const it = itinerario >= 0 ? dados.itinerarios[itinerario] : undefined;
  if (it === undefined) return '';
  return it[1] !== '' ? it[1] : it[0];
}

/**
 * O itinerário para o PAINEL: `Loire Valley (IT-BCN5PAR)`, ou só o código quando não tem nome.
 *
 * ⚠️ **Duas funções e não uma, e a diferença é o espaço que há.** Na **barra da grelha** cabem
 * ~20 caracteres e a identidade já está na sigla do cliente ao lado, logo mostra-se um só texto.
 * No **painel** e no **cartão** há espaço, e ali o código importa: é a chave que o planeador usa
 * para falar com o colega e para procurar. 📌 Esconder o código no sítio onde ele cabe seria
 * trocar informação por estética.
 */
export function itinerarioParaDetalhe(dados: DadosMapa, itinerario: number): string {
  const it = itinerario >= 0 ? dados.itinerarios[itinerario] : undefined;
  if (it === undefined) return '';
  return it[1] !== '' ? `${it[1]} (${it[0]})` : it[0];
}

/**
 * Rótulo da barra do itinerário.
 *
 * `sigla` sempre — é a identidade (V3), e é a **do cliente**. `nome` só quando a barra é larga o
 * suficiente para o mostrar sem cortar: mostrar meia palavra é pior do que não a mostrar. O limiar
 * em dias, e não em px, porque é o que o chamador conhece.
 *
 * ⚠️⚠️ **O `nome` PASSOU A SER O DO ITINERÁRIO, e não o do cliente (2026-08-14).**
 *
 * 📗 Cliente, 2026-08-13: *«no mapa, na primeira linha de cada viagem aparece as iniciais do
 * cliente e à frente o nome do cliente — eu quero que em vez do nome do cliente passe a aparecer o
 * **nome do itinerário**»*.
 *
 * 📌 **A sigla continua a ser a do CLIENTE, e é deliberado:** P-30 diz que *«a cor serve para
 * varrer o mapa; a sigla é a identidade»*, e a identidade que os planeadores procuram numa linha é
 * o cliente. O que muda é a segunda metade da etiqueta, que era redundante — a sigla e o nome do
 * cliente dizem a mesma coisa duas vezes, e o itinerário dizia-a zero.
 *
 * ⚠️ **Sem nome de itinerário mostra-se o CÓDIGO** (`IT-BCN5PAR`), e não o nome do cliente. É
 * feio e é honesto: dizer «Iberia Grand Tours» sobre uma barra cujo rótulo devia ser o itinerário
 * fazia o ecrã afirmar que aquele é o nome do circuito. 📌 A saída é dar nome aos itinerários na
 * Gestão — que já é possível.
 */
export function rotuloItinerario(
  dados: DadosMapa,
  cliente: number,
  dias: number,
  /**
   * O índice do itinerário — campo 5 da célula, ou 12 na viagem que parte num dia de encaixe.
   *
   * ⚠️ **Opcional, e `-1` reproduz o comportamento de antes de haver nome:** só a sigla. Existe
   * assim porque há chamadores (testes antigos, e a barra de um dia sem itinerário) para quem a
   * pergunta não se aplica — e obrigá-los a passar `-1` explicitamente não acrescentava garantia.
   */
  itinerario = -1,
): { readonly sigla: string; readonly nome: string } {
  const c = cliente >= 0 ? dados.clientes[cliente] : undefined;
  const sigla = c?.[0] ?? '—';
  // 4 dias ≈ 160 px. Abaixo disso só cabe a sigla.
  const nome = dias >= 4 ? nomeOuCodigoDoItinerario(dados, itinerario) : '';
  return { sigla, nome };
}

// ---------------------------------------------------------------------------
// ⚠️⚠️ B1 · O NOME DE UM CONDUTOR ESCREVE-SE NUM SÍTIO SÓ
// ---------------------------------------------------------------------------
//
// 📗 Cliente, 2026-09-11: *«Bloquear linha quando deixa de estar activo motorista ou
//    autocarro… no mapa uma nota com um ponto de interrogação à frente da alocação do
//    motorista.»*
//
// ⚠️⚠️ **A REGRA, e não o sintoma:** *«o nome de um condutor sai SEMPRE pela mesma
//    formulação, e é ela que decide se leva a marca»*. O sintoma seria «falta o `?` na barra
//    da banda 3»; a regra apanha os outros dezasseis sítios.
//
// 📌 **Varrido consulta a consulta e não por ficheiro** (regra 8, ponto 2), a 2026-09-11:
//    **17 leituras cruas de `dados.motoristas[…]` em 3 ficheiros** — 11 em `celula.ts` (o
//    cartão do cursor, o painel de detalhe, a decisão do intervalo, as duas viagens de um
//    dia de rotação), 5 em `Mapa.tsx` (a barra do condutor, o condutor principal da coluna
//    da esquerda ×2, e o filtro dos motoristas ×3) e 1 em `Consultas.tsx`. Corrigir só a
//    barra deixava dezasseis sítios a afirmar, com um nome limpo, que a pessoa continua na
//    empresa.
//
// ⚠️ **O `?` vai À FRENTE**, como o cliente pediu, e com um espaço: `«? Silva»`. Colado
//    (`«?Silva»`) lê-se como parte do nome.
//
// ⚠️⚠️ **E NÃO se marca a ausência.** Um índice negativo é «por atribuir» e devolve `''`
//    exactamente como antes — pôr `?` aí fazia o mapa dizer «esta pessoa saiu da empresa»
//    sobre um dia em que **ninguém** foi escolhido, que é a família F-33: ignorância
//    apresentada como facto. São coisas diferentes e não podem ter o mesmo sinal.
//
// 📌 Guardado por `nome-de-condutor-numa-formulacao.test.ts`, que recusa a 18.ª leitura crua
//    e prova que apanha o excerto real que estava aqui.

/** O prefixo do condutor que já não está na empresa. Uma só definição, e os testes leem-na. */
export const MARCA_INACTIVO = '? ';

/**
 * O nome a MOSTRAR de um condutor, com a marca quando ele já não está activo.
 *
 * ⚠️ `''` quando o índice é negativo («por atribuir») ou quando não há nome — que é o
 * comportamento que os dezassete sítios já tinham, palavra por palavra.
 */
export function nomeDoMotorista(dados: DadosMapa, i: number): string {
  if (i < 0) return '';
  const nome = dados.motoristas[i] ?? '';
  // ⚠️ **A marca aparece mesmo com o nome vazio.** Medido (B18): os 75 condutores reais da
  //    GVA não têm diminutivo, e um nome pode chegar vazio; devolver `''` aqui apagava a
  //    única pista de que a pessoa saiu — e o mapa mostrava um espaço em branco tranquilo.
  return dados.motoristasInactivos.includes(i) ? `${MARCA_INACTIVO}${nome}` : nome;
}

/**
 * Rótulo da barra do motorista.
 *
 * Na tripulação dupla mostram-se **os dois** quando cabem. Dizer só um nome
 * esconderia que o regime legal de repouso é outro (art. 8.º n.º 5).
 */
export function rotuloMotorista(
  dados: DadosMapa,
  motorista: number,
  motorista2: number,
  dias: number,
): string {
  const a = nomeDoMotorista(dados, motorista);
  if (motorista2 < 0) return a;
  const b = nomeDoMotorista(dados, motorista2);
  return dias >= 3 ? `${a} + ${b}` : `${a} +`;
}

/**
 * O dia na banda da localidade: o texto e o sentido do movimento, separados.
 *
 * Separados de propósito. A versão anterior concatenava `↓` ao texto, o que
 * obrigava a cor e o tamanho da seta a serem os do texto e impedia usar um ícone
 * de traço. Agora quem desenha decide como mostrar o sentido.
 */
export function rotuloDia(
  dados: DadosMapa,
  celula: CelulaCompacta,
): {
  readonly local: string;
  /**
   * Onde os PASSAGEIROS entram, no primeiro dia da viagem. `''` nos outros dias.
   *
   * Um dia pode ter DOIS locais: onde o grupo embarca e onde se pernoita depois
   * do primeiro dia — e não é o mesmo sítio. Medido na folha real: **741 de
   * 15 940 células de localidade** trazem o padrão `A/B`, **4,6 %**. Foi o
   * cliente a apontá-lo.
   */
  readonly embarque: string;
  readonly sentido: 'chegada' | 'partida' | null;
  /**
   * ⚠️ A cidade é PRESUMIDA (migração 093) — e `local` já traz o `?` colado. Vem à parte para
   * quem desenha poder pôr o `title` (`TITULO_DESTINO_PRESUMIDO`) sem voltar a ler a bandeira.
   */
  readonly presumido: boolean;
} {
  const b = celula[4];
  // Na grelha é o CÓDIGO: é o que cabe. O nome completo vai para o painel.
  // ⚠️⚠️ **O `?` do local presumido vai NO MESMO TEXTO, e é uma decisão de desempenho.** Um
  //    `<span>` próprio era mais um nó no último dia de cada viagem sem largada — e o orçamento
  //    do R1 (ADR-001) mede nós por dia. Colado ao código custa zero nós, e lê-se como a ficha e
  //    a tira dos dias o escrevem (`MAD?`), pela MESMA função do domínio.
  const codigo = codigoLocal(dados, celula[1]);
  const presumido = codigo !== '' && (b & BANDEIRA.LOCAL_PRESUMIDO) !== 0;
  const local = presumido ? comMarcaDePresumida(codigo) : codigo;
  const embarque = codigoLocal(dados, celula[9]);
  const sentido =
    (b & BANDEIRA.MOVIMENTO_IN) !== 0
      ? ('chegada' as const)
      : (b & BANDEIRA.MOVIMENTO_OUT) !== 0
        ? ('partida' as const)
        : null;
  return { local, embarque, sentido, presumido };
}

/**
 * `MAD` — o código, que é o que cabe numa coluna de 40 px.
 *
 * A grelha usa o código; o painel e o cartão usam `nomeLocal`, com o nome completo.
 */
export function codigoLocal(dados: DadosMapa, indice: number): string {
  return indice >= 0 ? (dados.localidades[indice]?.[0] ?? '') : '';
}

/**
 * `MAD — Madrid`, ou só o código quando não há nome.
 *
 * ⚠️ Pedido do cliente: *«nos detalhes quero que apareça, para além das 3 letras de
 * definição do local, o nome completo, para não existirem dúvidas»*. E a dúvida é
 * real, não hipotética: na folha há `SLM` Salamanca ao lado de `SVQ` Sevilha, `BJZ`
 * Badajoz ao lado de `BIO` Bilbao, e códigos internos que não são UN/LOCODE nenhum.
 */
export function nomeLocal(dados: DadosMapa, indice: number): string {
  const l = dados.localidades[indice];
  if (l === undefined) return '';
  return l[1] === '' || l[1] === l[0] ? l[0] : `${l[0]} — ${l[1]}`;
}

/**
 * As duas horas do dia de rotação: chegada em cima, partida em baixo.
 *
 * Só as horas — o sentido é dado por ícone, não por um caractere de seta colado
 * ao texto. Vazio quando a hora não existe, para o desenho não inventar valores.
 */
export function resumoRotacao(
  movimento: readonly [string, string] | null,
): { readonly chegada: string; readonly partida: string } {
  return {
    chegada: movimento === null ? '' : horaDe(movimento[0]),
    partida: movimento === null ? '' : horaDe(movimento[1]),
  };
}

/**
 * Resumo curto, para o cartão que segue o cursor.
 *
 * Três a cinco linhas: o que responde a «que serviço é este?» sem obrigar a abrir
 * o painel. O detalhe completo está em `detalhe`.
 */
/**
 * O dia desta viagem que traz `bandeira`, ou `-1`.
 *
 * ⚠️⚠️ **Existe porque o SINAL e a EXPLICACAO vivem em sitios diferentes:** a bandeira esta num
 * dia, o `!` esta na barra do itinerario, e a barra atravessa vários dias. Sem isto, so o dia
 * exacto da bandeira explicava o sinal — e numa viagem de oito dias havia sete em que passar o
 * rato nao dizia nada.
 *
 * 📌 A viagem delimita-se pela CORRIDA (`celula[10]`), que e a chave que o mapa usa para
 * desenhar a barra — F-19. Usar a rota juntava viagens diferentes do mesmo itinerario.
 */
function diaComBandeira(
  dados: DadosMapa,
  viatura: number,
  dia: number,
  bandeira: number,
): number {
  const linha = dados.grelha[viatura] ?? [];
  const corrida = linha[dia]?.[10] ?? -1;
  if (corrida < 0) return ((linha[dia]?.[4] ?? 0) & bandeira) !== 0 ? dia : -1;
  // Para tras e para a frente enquanto for a mesma corrida.
  let d0 = dia;
  while (d0 > 0 && (linha[d0 - 1]?.[10] ?? -1) === corrida) d0--;
  let d1 = dia;
  while (d1 + 1 < linha.length && (linha[d1 + 1]?.[10] ?? -1) === corrida) d1++;
  for (let d = d0; d <= d1; d++) if (((linha[d]?.[4] ?? 0) & bandeira) !== 0) return d;
  return -1;
}

/** `12` — o dia do mes, para nomear um dia numa frase. */
function diaDoMesDe(dados: DadosMapa, dia: number): number {
  return dados.diaDoMes[dia] ?? dia;
}

// ============================================================================
// ⚠️⚠️ «ALTERADA HÁ N MIN POR …» — UM SÓ SÍTIO, e a razão é a contagem
// ============================================================================
//
// 📄 §11 linha O, peça **P1**. A migração 090 pôs a âncora na linha; isto é o que a mostra.
//
// 📌 **Porque é um INVÓLUCRO e não uma linha acrescentada no fim de cada função:** o `resumo`
//    tem **três** `return` e o `detalhe` **dois**. Pôr a frase antes de cada um eram cinco
//    cópias, e a que divergisse seria a que ninguém corre — regra 8. Assim há **uma**
//    formulação e nenhum ramo pode escapar-lhe, incluindo os que hoje saem cedo (a célula de
//    oficina/avaria, que é precisamente uma das que mais se mexe).
//
// ⚠️ **`agora` é parâmetro com omissão** — as funções continuam a chamar-se como antes, e as
//    provas podem fixar o instante em vez de dependerem do relógio da máquina onde correm.
// ============================================================================

function resumoDaCelula(dados: DadosMapa, viatura: number, dia: number): readonly string[] {
  const celula = dados.grelha[viatura]?.[dia];
  const v = dados.viaturas[viatura];
  const data = dataUtcDoIndice(janelaDe(dados), dia).toISOString().slice(0, 10);
  const linhas: string[] = [`${v?.numero ?? '?'} · ${data}`];

  if (celula == null) {
    linhas.push('sem serviço');
    return linhas;
  }

  const [cliente, localidade, motorista, , bandeiras, itinerario, motorista2, estadoViatura] =
    celula;

  if (estadoViatura >= 0) {
    linhas.push(dados.estadosViatura[estadoViatura] ?? '');
    if (localidade >= 0) linhas.push(nomeLocal(dados, localidade));
    return linhas;
  }

  if (cliente >= 0) {
    const c = dados.clientes[cliente];
    linhas.push(`${c?.[0] ?? ''} — ${c?.[1] ?? ''}`);
  }
  if (itinerario >= 0) linhas.push(nomeOuCodigoDoItinerario(dados, itinerario));
  if (celula[10] >= 0) linhas.push(`reserva ${dados.corridas[celula[10]] ?? ''}`);
  // ---- ⚠️⚠️ §8.3 · ESTE DIA NAO E DE TRABALHO (`parado_no_servico`, migracao 072) --------
  //
  //   📗 §8.3: um servico de 12 dias com 2 em Marrocos **nao sao 12 dias de trabalho**.
  //
  // ⚠️ **VEM ALTO no cartao, logo apos a identidade da viagem**, porque muda a leitura de tudo o
  //   que vem abaixo: o condutor consta, a pernoita consta, e nenhum deles e trabalho. 📌 O
  //   cartao e a leitura que responde **sem clique** (P-28) — deixar isto so no Detalhe, que abre
  //   a pedido, era ter o dado e nao o dizer onde a pergunta se faz.
  if ((bandeiras & BANDEIRA.PARADO_NO_SERVICO) !== 0) {
    linhas.push(
      'Este dia NÃO É de trabalho — a viatura está comprometida com esta viagem e não ' +
        'trabalha (o grupo seguiu por outro meio, ou o dia é de outro operador). O dia continua ' +
        'ocupado; não conta como dia trabalhado.',
    );
  }
  if (celula[9] >= 0) {
    // Dois locais: entrada dos passageiros e pernoita. Ver `rotuloDia`.
    linhas.push(
      `entram em ${nomeLocal(dados, celula[9])} · dormem em ` +
        `${localidade >= 0 ? nomeLocal(dados, localidade) : 'local por definir'}`,
    );
  } else {
    // ⚠️ A pernoita aparece SEMPRE, mesmo em falta. Foi o cliente a exigi-lo: «o
    // pernoitar interessa para o motorista e tem de ser definido em todos os dias
    // das viagens e dias de descanso fora de casa, e tem de ficar sempre presente
    // nos detalhes, uma vez que por vezes não é indicado».
    //
    // ⚠️⚠️ **2026-08-26 · «Autocarro parado em X», não «dormem em X», quando não há
    //    viagem nenhuma.** 📗 Cliente: *«quando a viagem acaba e fica no "parado
    //    fora"… diz "Dormem em X". Gostaria que dissesse "Autocarro parado em X"»*.
    //    "Dormem" descreve a pernoita de uma viagem a decorrer; num dia parado não
    //    há grupo nenhum a dormir — é só a viatura à espera do serviço seguinte.
    const parado = natureza(celula)?.tipo === 'parado';
    // ⚠️ Migração 093 · o cartão responde sem clique (P-28): a cidade presumida leva o `?` aqui
    //    também, e a razão numa linha própria — a mesma frase do detalhe.
    const presumido = localidade >= 0 && (bandeiras & BANDEIRA.LOCAL_PRESUMIDO) !== 0;
    const onde = presumido
      ? comMarcaDePresumida(nomeLocal(dados, localidade))
      : nomeLocal(dados, localidade);
    linhas.push(
      localidade >= 0
        ? parado
          ? `autocarro parado em ${onde}`
          : `dormem em ${onde}`
        : 'pernoita POR DEFINIR',
    );
    if (presumido) linhas.push(TITULO_DESTINO_PRESUMIDO);
  }
  if (motorista >= 0) {
    linhas.push(
      motorista2 >= 0
        ? `${nomeDoMotorista(dados, motorista)} + ${nomeDoMotorista(dados, motorista2)} (dupla)`
        : nomeDoMotorista(dados, motorista),
    );
  }
  // ⚠️⚠️ **OS DOIS CONDUTORES DO DIA DE ROTAÇÃO — 📗 «ter os dois indicados nesse dia».**
  //
  //   O cartão é a leitura mais rápida do mapa: responde sem clique nenhum. Num dia em que o
  //   condutor muda na rotação, mostrar só um fazia o cartão **afirmar** quem conduz o dia — e
  //   metade dele é de outra pessoa.
  //
  // ⚠️ **`troca` e NÃO `dupla`.** A dupla são dois ao mesmo tempo no mesmo autocarro (art. 8.º
  //   n.º 5, regime de repouso diferente); isto são dois em **sequência**. As duas palavras têm
  //   de ser diferentes no ecrã, senão o cartão descreve o regime legal errado.
  if (celula[14] >= 0 && celula[14] !== motorista) {
    linhas.push(
      `↳ ${nomeDoMotorista(dados, celula[14])} na que parte (troca)` +
        (celula[15] >= 0 ? ` + ${nomeDoMotorista(dados, celula[15])} (dupla)` : ''),
    );
  }
  if ((bandeiras & BANDEIRA.RESERVA) !== 0) linhas.push('RESERVA');
  // ⚠️ 📗 «um pisco que diga: ok, aqui já não quero mexer mais». Uma marca no mapa sem palavras
  //    lê-se como decoração — e esta tem consequência: o motor não a propõe para nada.
  if ((bandeiras & BANDEIRA.FIXADO) !== 0) linhas.push('FIXADO — não entra em alterações');
  // ⚠️⚠️ **A RAZAO DO `!`, e nao estava aqui.**
  //
  //   📗 Cliente, 2026-08-10: *«o que significam os pontos de exclamacao nos itinerarios?
  //   Ao passar o rato por cima desse ponto de exclamacao devera aparecer uma nota, por
  //   exemplo, a explicar a razao»*.
  //
  // ⚠️⚠️ **A explicacao existia — no `detalhe()`, que alimenta o PAINEL, e o painel abre a
  //   pedido e por omissao esta fechado.** O cartao do cursor, que e o que responde ao
  //   rato (P-28), nunca a dizia. 📌 E o mesmo padrao da semana: a informacao calculada e
  //   sem leitor no caminho onde a pergunta e feita. Duas funcoes a descrever a mesma
  //   celula, e a diferenca entre elas era invisivel a quem le o codigo.
  // ⚠️⚠️ **A RAZAO DO `!`, EM QUALQUER DIA DA VIAGEM — e escrita em portugues corrente.**
  //
  //   📗 Cliente, 2026-08-10: *«nao percebo o que significa: marcado na origem, mas SEM
  //   partida no dia seguinte — nao e rotacao»*.
  //
  // ⚠️⚠️ **Tinha razao: a frase eram tres termos internos e nenhuma instrucao.** «Na origem»
  //   e o ficheiro de importacao, «rotacao» e vocabulario nosso, e «partida» e uma coluna do
  //   modelo. 📌 E acabava a NEGAR uma coisa que o leitor nunca afirmou — «nao e rotacao» so
  //   informa quem ja sabia o que rotacao era. O que o planeador precisa de saber e o que se
  //   passa e o que fazer.
  //
  // ⚠️⚠️ **E aparece em QUALQUER dia da viagem, nomeando o dia em causa.** A bandeira esta num
  //   dia; o `!` esta na barra, que atravessa vários. Só o dia da bandeira explicar o sinal
  //   punha a nota a sete dias do sinal — foi por isso que eu tinha movido o `!`, e mover o
  //   sinal era a solucao errada para o problema certo.
  const diaSuspeito = diaComBandeira(dados, viatura, dia, BANDEIRA.ROTACAO_SUSPEITA);
  if (diaSuspeito >= 0) {
    const qual =
      diaSuspeito === dia ? 'Este dia' : `O dia ${diaDoMesDe(dados, diaSuspeito)} desta viagem`;
    linhas.push(
      `! ${qual} estava marcado como «chega e parte no mesmo dia», mas no dia ` +
        'seguinte a viatura não tem serviço — logo não parte de lado nenhum. É só uma ' +
        'chegada. Confirme se falta lançar o serviço seguinte.',
    );
  }
  // 📗 fonte 6.8: a nota «aparece também no tooltip». Este cartão É o tooltip (P-28).
  const nota = dados.notas[`${viatura}:${dia}`];
  if (nota !== undefined) linhas.push(`✎ ${nota}`);
  return linhas;
}

/**
 * Detalhe de um servico SUBCONTRATADO, para o mesmo painel da frota.
 *
 * 📗 Cliente, 2026-08-10: *«tem de dar para ver detalhes de igual forma, tem de permitir
 * clicar»*.
 *
 * ⚠️⚠️ **«De igual forma» e o GESTO, nao o conteudo.** Clicar fixa, o painel abre, e a
 * informacao esta la — exactamente como na frota. Mas metade do que a frota mostra **nao
 * existe aqui e a outra metade nao e nossa** (D-002), e inventar linhas vazias para as
 * colunas em falta era o pior resultado possivel: um painel que parece completo sobre uma
 * coisa que nao sabemos.
 *
 * 📌 Por isso **o que NAO se aplica esta escrito**, em vez de omitido. Um campo ausente
 * le-se como «nao ha nada a dizer»; escrito, le-se como «isto e de outra empresa» — que e a
 * unica coisa que ha para dizer.
 *
 * ⚠️⚠️ **E nao ha ACCOES.** Nenhum dos botoes da frota faz sentido: nao se edita o servico de
 * outra empresa, nao se marca oficina na viatura dela, nao se lhe atribui um condutor nosso.
 * O painel recebe `alvo: null` neste caso, e por isso os botoes — que todos verificam
 * `alvo !== null` — desaparecem sem uma unica condicao nova.
 */
export function detalheSub(
  dados: DadosMapa,
  sub: number,
  /**
   * ⚠️⚠️ **A LINHA, e não a grelha da empresa — é o C₂.** Antes lia-se
   * `dados.grelhaSubcontratados[sub]`, que num dia com nove serviços trazia os nove: a função
   * listava-os todos e anunciava o intervalo do **primeiro** (`extentoDoServico(linha, dia, 0)`)
   * como se fosse o do conjunto. 📌 Com uma linha por serviço a pergunta tem uma só resposta.
   * Continua a aceitar a grelha de uma empresa inteira, e a comportar-se bem: é o que torna o
   * parâmetro uma restrição do chamador e não um novo caso a tratar aqui.
   */
  linha: readonly (readonly CelulaCompacta[])[],
  dia: number,
): readonly (readonly [string, string])[] {
  const sc = dados.subcontratados[sub];
  const servicos = linha[dia] ?? [];
  const data = dataUtcDoIndice(janelaDe(dados), dia);
  const semana = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'][
    data.getUTCDay()
  ];
  const pares: [string, string][] = [
    ['subcontratado', `${sc?.nome ?? '—'} — empresa externa`],
    ['dia', `${data.toISOString().slice(0, 10)} · ${semana ?? ''}`],
  ];

  if (servicos.length === 0) {
    pares.push(['serviço', 'sem serviço neste dia']);
    return pares;
  }

  // ⚠️⚠️ **O INTERVALO, e nao so este dia.** Um servico sao dias; dizer «10 de Agosto» sobre uma
  //   barra de cinco obriga a arrastar o rato pelos outros quatro para saber onde acaba.
  //
  // ⚠️⚠️ **A REGRA VEM DO MÓDULO DAS BARRAS, e aqui estava a TERCEIRA cópia dela.** As três
  //   traziam a condição extra «e o mesmo NÚMERO de serviços simultâneos», que existia porque a
  //   barra levava uma contagem por dia. 📌 Com B28 a barra é por SERVIÇO, a contagem morreu — e
  //   esta cópia passaria a anunciar um intervalo mais curto do que o que está desenhado.
  const { d0, d1 } = extentoDoServico(linha, dia, 0);
  const iso = (d: number): string =>
    dataUtcDoIndice(janelaDe(dados), d).toISOString().slice(0, 10);
  pares.push(['serviço', `${iso(d0)} a ${iso(d1)} · ${d1 - d0 + 1} dia(s)`]);

  // ⚠️⚠️ **VARIOS SERVICOS NO MESMO DIA e o caso que justifica a seccao existir** (D-002): um
  //   subcontratado e uma EMPRESA e pode ter nove ao mesmo tempo.
  //
  // ⚠️ **Com o C₂ o chamador passa UMA LINHA, logo isto não dispara a partir do mapa** — e
  //   fica, porque a função continua a aceitar a grelha de uma empresa inteira e porque uma
  //   lista sem o aviso leria-se como «um serviço» sobre nove. 📌 O que a secção deixou de
  //   precisar é da *contagem* como substituto do desenho: os nove estão nas nove linhas.
  if (servicos.length > 1) {
    pares.push([
      'atenção',
      `${servicos.length} serviços em simultâneo neste dia — é uma empresa, não um autocarro`,
    ]);
  }
  servicos.forEach((c, i) => {
    const nome = dados.clientes[c[0]]?.[1] ?? 'cliente por identificar';
    const sigla = dados.clientes[c[0]]?.[0] ?? '—';
    const rot = servicos.length > 1 ? `cliente ${i + 1}` : 'cliente';
    pares.push([rot, `${sigla} — ${nome}`]);
    if (c[1] >= 0) pares.push(['localidade', nomeLocal(dados, c[1])]);
    if (c[5] >= 0) pares.push(['itinerário', itinerarioParaDetalhe(dados, c[5])]);
    if (c[10] >= 0) {
      const refC = dados.corridas[c[10]];
      pares.push(['referência da viagem', refC ?? '']);
      // ⚠️ A cor manual (migração 055) — só para quem já a escolheu perceber que esta
      // viagem não segue a cor do cliente, sem ter de abrir a janela para o descobrir.
      if (refC !== undefined && dados.coresCorrida[refC] !== undefined) {
        pares.push(['cor', 'personalizada — não é a do cliente']);
      }
    }
    // ⚠️⚠️ **A RESERVA DIZ-SE POR PALAVRAS e nao so pela textura.** Uma reserva confundida com
    //   servico firme faz alguem deixar de a confirmar — e a textura, sozinha, e um sinal que
    //   se pode nao conhecer. A legenda explica-a; o painel escreve-a.
    if ((c[4] & BANDEIRA.RESERVA) !== 0) {
      pares.push(['estado', 'RESERVA — ainda não confirmado com a empresa']);
    } else {
      pares.push(['estado', 'confirmado']);
    }
  });

  if (sc !== undefined && sc.base >= 0) {
    pares.push([
      'base da empresa',
      `${nomeLocal(dados, sc.base)} — é daqui que o motor conta a deslocação até ao serviço`,
    ]);
  }

  // ⚠️⚠️ **O ITINERARIO TODO, DIA A DIA — 📗 *«tem de aparecer igualmente todo o itinerario, nem
  //   que seja para monitorizacao»*.**
  //
  //   ⚠️⚠️ **ESTE COMENTARIO JA DIZIA O CONTRARIO DUAS VEZES, e as duas razoes morreram.**
  //   Afirmou primeiro que «a linha do subcontratado tem UMA banda de 30 px … e tres bandas por
  //   linha nao resolveriam isso, multiplicariam-no por tres»; o B28 e depois o C₂ deram-lhe
  //   **uma linha por servico**. Afirmou a seguir que «a barra da grelha traz o PRIMEIRO e o
  //   ULTIMO local», e a 2026-09-16 a grelha passou a trazer **a cidade de CADA dia**, em banda
  //   propria (`BandaDeLocais` em `Mapa.tsx`) — 📗 *«com as cidades por dias seguindo a mesma
  //   estrutura»*. A linha mede agora **36 px**: duas bandas, sem a do motorista.
  //
  //   ⚠️ **O que continua verdade, e e o que sustenta este painel:** a grelha mostra o CODIGO
  //   da cidade, e o itinerario dia a dia — nome completo, sem abreviar — nao cabe em 40 px por
  //   dia. Essa parte e deste painel, e nao da grelha.
  //
  // ⚠️⚠️ **Logo o itinerario tem de aparecer AQUI, e nao na grelha.** Um servico subcontratado
  //   nao se pode monitorizar so pela barra: ela diz o cliente e as datas, e a pergunta de
  //   monitorizacao e «por onde e que ele anda».
  //
  // ⚠️⚠️ **Dias sem localidade registada aparecem, e dizem-no.** Omiti-los dava um itinerario
  //   com buracos que se leem como «nao ha viagem nesse dia» — e o que ha e falta de registo.
  //   E a mesma regra que a frota aplica a pernoita (5,9 % dos dias de corrida sem local).
  if (d1 > d0 || servicos.length > 0) {
    const passos: string[] = [];
    let anterior = '';
    for (let d = d0; d <= d1; d++) {
      const doDia = linha[d] ?? [];
      const locs = doDia
        .map((c) => (c[1] >= 0 ? (dados.localidades[c[1]]?.[0] ?? '?') : '?'))
        .join('/');
      // ⚠️⚠️ **Nao se repete o mesmo local em dias seguidos** — a mesma regra da grelha: o que
      //   interessa ver e o MOVIMENTO, e «MAD MAD MAD MAD» esconde-o em vez de o mostrar.
      if (locs === anterior) continue;
      anterior = locs;
      const dm = dados.diaDoMes[d] ?? d;
      passos.push(`${dm}: ${locs}`);
    }
    pares.push([
      'itinerário (por onde anda)',
      passos.length === 0 ? 'sem localidades registadas' : passos.join('  →  '),
    ]);
  }

  // ⚠️⚠️ **O QUE NAO SE APLICA, escrito.** Ver o cabecalho desta funcao.
  pares.push(['condutor', 'é da empresa subcontratada — não é um condutor nosso']);
  pares.push(['estado da viatura', 'não sabemos: a frota é dela']);
  pares.push([
    'pernoita e alojamento',
    'a cargo da empresa subcontratada — não entra nos nossos custos',
  ]);
  return pares;
}

/**
 * Detalhe completo, para o painel.
 *
 * Devolve pares para o painel os poder desenhar como lista de definições, em vez
 * de um bloco de texto — que era o que estava e que ninguém lê.
 */
function detalheDaCelula(
  dados: DadosMapa,
  viatura: number,
  dia: number,
  minutosRotacaoMinimo: number,
): readonly (readonly [string, string])[] {
  const celula = dados.grelha[viatura]?.[dia];
  const v = dados.viaturas[viatura];
  const data = dataUtcDoIndice(janelaDe(dados), dia);
  const iso = data.toISOString().slice(0, 10);
  const semana = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'][
    data.getUTCDay()
  ];

  const pares: [string, string][] = [
    ['viatura', `${v?.numero ?? '?'} · ${v?.matricula ?? '?'} · ${v?.lotacao ?? '?'} lugares`],
    ['data', `${iso} (${semana})`],
  ];

  if (celula == null) {
    pares.push(['serviço', 'nenhum — viatura livre neste dia']);
    return pares;
  }

  // 📗 fonte 6.8, D-079. ⚠️ A NOTA VEM NO TOPO e não no fim: é o que alguém escreveu à mão
  // porque o modelo não tinha campo para o guardar, e é a informação com maior
  // probabilidade de ser a razão de se estar a olhar para esta célula.
  const nota = dados.notas[`${viatura}:${dia}`];
  if (nota !== undefined) pares.push(['nota', nota]);

  const [cliente, localidade, motorista, estado, bandeiras, itinerario, motorista2, estadoViatura, meio] =
    celula;

  // ---- ⚠️⚠️ §8.3 · ESTE DIA NAO E DE TRABALHO (`parado_no_servico`, migracao 072) --------
  //
  //   ⚠️ **Antes do resto**, e nao no fim: o `estado da viatura`, o cliente e a rota descrevem o
  //   servico, e esta linha diz que **este** dia dele nao e trabalho. Escrita depois, lia-se como
  //   uma ressalva; escrita aqui, enquadra o que vem a seguir.
  //
  // 📌 **E diz a CONSEQUENCIA e nao so o facto.** «Parado no servico» e vocabulario do modelo;
  //   o que o planeador precisa de saber e que o dia continua ocupado **e** que nao entra nos
  //   contadores — as duas coisas, porque cada uma sozinha manda tomar a decisao errada.
  if ((bandeiras & BANDEIRA.PARADO_NO_SERVICO) !== 0) {
    pares.push([
      'este dia',
      'NÃO é de trabalho — a viatura está comprometida com esta viagem e não trabalha (o grupo ' +
        'seguiu por outro meio, ou o dia é de outro operador). O dia continua ocupado e não ' +
        'conta como dia trabalhado nos contadores do art. 8.º.',
    ]);
  }
  if (estadoViatura >= 0) {
    pares.push(['estado da viatura', dados.estadosViatura[estadoViatura] ?? '?']);
  }
  if (cliente >= 0) {
    const c = dados.clientes[cliente];
    pares.push(['cliente', `${c?.[0] ?? ''} — ${c?.[1] ?? ''}`]);
  }
  if (itinerario >= 0) pares.push(['rota', itinerarioParaDetalhe(dados, itinerario)]);
  if (celula[10] >= 0) {
    // A CORRIDA identifica a viagem; a rota é só a assinatura origem/destino.
    // Medido: 2 679 assinaturas para 15 132 corridas.
    const referenciaDaViagem = dados.corridas[celula[10]];
    pares.push(['referência da viagem', referenciaDaViagem ?? '']);
    // ⚠️ A cor manual (migração 055) — só um sinal de que esta viagem não segue a cor
    // do cliente; a janela «Cor da viagem…» é onde se muda.
    if (referenciaDaViagem !== undefined && dados.coresCorrida[referenciaDaViagem] !== undefined) {
      pares.push(['cor', 'personalizada — não é a do cliente']);
    }

    // ---- ⚠️⚠️ A MESMA VIAGEM NOUTRAS VIATURAS -----------------------------
    //
    // 📗 Cliente, 2026-08-12: *«preciso que seja possível subdividir um itinerário por diferentes
    //    viaturas… tal como acontece já na escala real. Já existe alguma forma de o fazer, como
    //    funciona na escala real, confirma-se que o fazem??»*
    //
    // ✅ **CONFIRMA-SE, e está medido (F-22): 197 das 461 referências reais (43 %) aparecem em mais
    //    do que um autocarro, 46 delas em dias SOBREPOSTOS** — até quatro autocarros na mesma
    //    referência. São dois casos operacionalmente opostos: **sequencial** (uma viatura faz a
    //    primeira parte, outra a segunda — a rendição) e **simultâneo** (o grupo não cabe num
    //    autocarro e vão dois ao mesmo tempo).
    //
    // ⚠️⚠️ **E JÁ SE FAZIA, num só gesto — o que faltava era VER-SE.** «Trocar viatura» move um
    //    INTERVALO de dias e **não toca na `referencia`** (o `UPDATE` de `mover.ts` só mexe em
    //    `viatura_id`, `subcontratado_id`, `estado_viatura` e `papel_no_dia`), logo a viagem fica
    //    nas duas viaturas ligada pela mesma corrida — que é exactamente o modelo da folha real.
    //    📌 **Partir (`dividir`) é a operação OPOSTA:** quebra a referência de propósito, e serve
    //    para quando a segunda metade passa a ser outro serviço.
    //
    // ⚠️ **Sem esta linha, subdividir era invisível:** as duas metades ficavam com a mesma cor e a
    //    mesma sigla em linhas diferentes de uma grelha de 250, e nada dizia que eram a mesma
    //    viagem. Quem subdividia perdia a segunda metade de vista — e o documento de 2026-08-08 já
    //    declarava que «uma vista única que mostre as duas metades ao mesmo tempo é ecrã novo, e
    //    não o fiz».
    //
    // 📌 Custo: uma varredura das 250 linhas **nos dias desta viagem**, e só quando o painel abre —
    //    nunca por quadro da grelha. O painel abre a pedido (P-28), logo isto não toca no p95.
    const outras: string[] = [];
    for (let w = 0; w < dados.grelha.length; w++) {
      if (w === viatura) continue;
      const l = dados.grelha[w] ?? [];
      let d0 = -1;
      let d1 = -1;
      for (let d = 0; d < dados.nDias; d++) {
        const c = l[d] ?? null;
        // ⚠️ Compara com os DOIS campos de corrida: num dia de rotação a viagem que parte vive no
        //    índice 13, e ignorá-lo perdia exactamente o limite entre serviços (migração 027).
        if (c !== null && (c[10] === celula[10] || c[13] === celula[10])) {
          if (d0 < 0) d0 = d;
          d1 = d;
        }
      }
      if (d0 >= 0) {
        const nd = dados.viaturas[w]?.numero ?? '?';
        const de = dataUtcDoIndice(janelaDe(dados), d0).toISOString().slice(5, 10);
        const ate = dataUtcDoIndice(janelaDe(dados), d1).toISOString().slice(5, 10);
        outras.push(d0 === d1 ? `${nd} (${de})` : `${nd} (${de} a ${ate})`);
      }
    }
    if (outras.length > 0) {
      // ⚠️ Diz **quantas** e **em que dias**: «também na 243» não deixa saber se é a continuação
      //    ou um segundo autocarro no mesmo dia, e as duas coisas exigem decisões diferentes.
      pares.push([
        'a mesma viagem também em',
        `${outras.join(' · ')} — subdividida por ${outras.length + 1} viaturas`,
      ]);
    }
  }
  // ---- O PRIMEIRO DIA DE UMA VIAGEM TEM DE DIZER TRÊS COISAS ---------------
  //
  // Exigência do cliente: *«em todas as novas viagens tem de ser indicado o local de
  // partida e hora, e ainda o local de pernoitar (relativo sempre ao motorista)»*.
  //
  // As três aparecem **mesmo quando faltam**, escritas como lacuna. Esconder um
  // campo em falta é garantir que ninguém o preenche — e estes três não são
  // decorativos: a hora é a do voo que o autocarro vai esperar, e a pernoita decide
  // se o repouso é legal (art. 8.º n.º 8).
  const movimento = dados.movimentos[viatura]?.[dia] ?? null;
  const primeiroDia = (bandeiras & BANDEIRA.PRIMEIRO_DIA) !== 0 && estadoViatura < 0;
  if (primeiroDia) {
    pares.push([
      'entrada dos passageiros',
      celula[9] >= 0
        ? nomeLocal(dados, celula[9])
        : localidade >= 0
          ? `${nomeLocal(dados, localidade)} (o mesmo do primeiro destino)`
          : 'POR DEFINIR',
    ]);
    const hora = horaDe(movimento?.[0] ?? '');
    pares.push([
      'hora de chegada do grupo',
      hora !== '' ? hora : 'POR DEFINIR — é a hora a que o voo aterra',
    ]);
  }

  if (localidade >= 0 && !primeiroDia) {
    // ⚠️ Migração 093 · a cidade PRESUMIDA diz-se com o `?` e com a razão, pela MESMA frase da
    //    ficha e da tira dos dias. Um detalhe que a escrevesse sem marca desmentia o mapa.
    if ((bandeiras & BANDEIRA.LOCAL_PRESUMIDO) !== 0) {
      pares.push(['local do dia', comMarcaDePresumida(nomeLocal(dados, localidade))]);
      pares.push(['local presumido', TITULO_DESTINO_PRESUMIDO]);
    } else {
      pares.push(['local do dia', nomeLocal(dados, localidade)]);
    }
  }

  // ---- A PERNOITA APARECE SEMPRE ------------------------------------------
  //
  // *«O pernoitar interessa para o motorista e tem de ser definido em todos os dias
  // das viagens e dias de descanso fora de casa, e tem de ficar sempre presente nos
  // detalhes, uma vez que por vezes não é indicado.»*
  //
  // Duas consequências no que se escreve:
  //
  // 1. **É do MOTORISTA, não da viatura.** O rótulo di-lo, porque a distinção tem
  //    efeito legal: é o repouso dele que a lei regula (art. 8.º), e a GVA é mais
  //    restritiva que a lei — o motorista **nunca** dorme no autocarro (D-060), logo
  //    toda a pernoita fora de casa é alojamento pago.
  // 2. **Aparece em falta.** Medido: 5,9 % dos dias dentro de uma corrida não têm
  //    localidade na folha real. Um campo que desaparece quando está vazio nunca é
  //    preenchido.
  //
  // ⚠️ Nos dias de oficina/avaria a viatura está parada mas o motorista **não está
  // com ela** — está em casa ou noutra viatura (D-055). A pernoita desses dias
  // pertence à linha de tempo do MOTORISTA, que o mapa da viatura não tem (P-09).
  // Diz-se isso em vez de inventar um local.
  //
  // ⚠️⚠️ **E SEM MOTORISTA ESCALADO NÃO SE AFIRMA UM CUSTO — defeito reportado a 2026-08-12.**
  //
  // 📗 Cliente: *«eu mesmo pondo que o motorista vai a casa… continua a aparecer pernoita fora da
  // base, alojamento pago. Resolva definitivamente este problema e perceba a causa.»*
  //
  // 📌 **A CAUSA, medida antes de tocar em nada:** esta frase saía **só** da localidade da
  // **viatura-dia** — e o rótulo diz «do MOTORISTA». Nos dias parados a viatura fica onde acabou
  // (52,4 % dos intervalos reais) e **não há condutor nenhum nela**: medido no `dev`, a V0003 nos
  // dias 2026-01-07 a 13 tem **zero** linhas em `alocacao_condutor`. Logo o painel afirmava uma
  // noite paga de uma pessoa que não estava identificada — e continuava a afirmá-la depois de o
  // planeador registar que o motorista **vai a casa**, porque essa decisão vive na linha dele
  // (`tipo_dia = 'em_casa'`, `viatura_id NULL`, D-087), que a grelha da viatura não tem onde ler.
  //
  // 📌 **É a mesma coisa que o cliente nos ensinou sobre o alojamento** — *«se o motorista na
  // viagem seguinte for outro não vai ter nunca de pagar… ele volta a casa; pode é o autocarro
  // ficar»*. O autocarro ficar em Barcelona e alguém dormir em Barcelona são **dois factos
  // diferentes**, e este campo estava a deduzir o segundo do primeiro.
  //
  // ⚠️ **Escala medida:** 1 179 dias-viatura registados fora da base, sem estado de viatura e
  // **sem condutor nenhum** — mais todos os dias de paragem inferida. Não era um caso isolado.
  //
  // ⚠️ **O rótulo NÃO muda** (D-086 exige que o campo esteja sempre no mesmo sítio, mesmo em
  // falta); muda o que ele afirma. E não se cala: dizer «não sei» aqui apagava a informação real,
  // que é **onde a viatura fica**.
  // ---- ⚠️⚠️ B02: A DECISÃO REGISTADA GANHA A TUDO (2026-08-22) --------------------
  //
  //   📗 Cliente: *«Eu registei que um motorista ficava no local e não guarda… os detalhes
  //   continua sem motorista… Não registou…»*
  //
  // ⚠️⚠️ **E tinha registado.** A decisão vive numa linha de `alocacao` com `motorista_id` e sem
  //   `viatura_id` (D-087) — a grelha da viatura descartava-a, e este campo continuava a afirmar
  //   *«sem motorista escalado, não há alojamento a somar»* sobre um facto registado. 📌 É a
  //   pior forma deste defeito: não é silêncio, é uma afirmação contrária à decisão que a pessoa
  //   acabou de tomar.
  //
  // ⚠️ Vem de `pernoitaDecidida` (migração 036, `viatura_contexto_id`) e **não** de uma
  //   inferência: repetir aqui a regra que o cartão do intervalo usa era a quinta implementação
  //   da mesma coisa. E ganha ao resto porque é a única coisa nesta lista que **alguém decidiu**.
  const decidida = dados.pernoitaDecidida[`${String(viatura)}:${String(dia)}`];
  if (decidida !== undefined) {
    const quem = nomeDoMotorista(dados, decidida.motorista);
    const onde = decidida.localidade >= 0 ? nomeLocal(dados, decidida.localidade) : null;
    pares.push([
      'pernoita do motorista',
      decidida.tipoDia === 'em_casa'
        ? `${quem} vai a casa${onde === null ? '' : ` (${onde})`} — sem alojamento a pagar. ` +
          'REGISTADO'
        : `${quem} fica no local${onde === null ? '' : ` (${onde})`} — alojamento pago. REGISTADO`,
    ]);
  } else if (estadoViatura >= 0) {
    pares.push([
      'pernoita do motorista',
      'não é desta viatura — nestes dias o motorista está em casa ou noutra viatura',
    ]);
  } else {
    const naBase = localidade >= 0 && localidade === dados.localidadeBase;
    pares.push([
      'pernoita do motorista',
      localidade < 0
        ? 'POR DEFINIR — obrigatório: decide se o repouso é legal (art. 8.º n.º 8)'
        : naBase
          ? `${nomeLocal(dados, localidade)} — na base, o motorista vai a casa: sem alojamento a pagar`
          : motorista >= 0
            ? `${nomeLocal(dados, localidade)} — fora da base: alojamento pago`
            : `${nomeLocal(dados, localidade)} — é onde fica a VIATURA. Sem motorista escalado neste dia, não há alojamento a somar: o autocarro fica, o motorista pode ir a casa`,
    ]);
  }

  // ⚠️ **QUANDO O DIA TEM DUAS VIAGENS, O RÓTULO TEM DE DIZER DE QUAL.** 📗 Cliente,
  //   2026-08-14: *«muitas vezes é no dia de rotação que se muda o motorista»*. Sem isto o
  //   painel dizia «motorista: X» num dia em que X conduz **metade** dele — e a outra metade
  //   é de outra pessoa, numa viagem de outro cliente.
  const temDuasViagens = celula[13] >= 0;
  const rotuloDoCondutor = temDuasViagens ? 'motorista da viagem que CHEGA' : 'motorista';
  if (motorista >= 0) {
    pares.push([rotuloDoCondutor, nomeDoMotorista(dados, motorista)]);
    if (motorista2 >= 0) {
      // «tripulação dupla» e não «2.º motorista»: a diferença muda o regime legal
      // de repouso, e quem lê o mapa tem de a ver.
      pares.push([
        '2.º condutor',
        `${nomeDoMotorista(dados, motorista2)} — tripulação dupla (art. 8.º n.º 5)`,
      ]);
    }
  } else if (estadoViatura < 0) {
    pares.push([rotuloDoCondutor, 'por atribuir']);
  } else {
    pares.push([rotuloDoCondutor, 'em casa ou noutra viatura']);
  }

  if (movimento !== null) {
    // A chegada do primeiro dia já foi dita acima, com o nome «hora de chegada do
    // grupo». Repeti-la aqui punha a mesma hora duas vezes no mesmo painel.
    if (movimento[0] !== '' && !primeiroDia) pares.push(['chegada', movimento[0]]);
    if (movimento[1] !== '') pares.push(['partida', movimento[1]]);
  } else if (
    (bandeiras & BANDEIRA.ULTIMO_DIA) !== 0 &&
    estadoViatura < 0 &&
    // ---- ⚠️⚠️ E TEM DE HAVER VIAGEM, o que parece óbvio e não estava escrito ----
    //
    //   `ULTIMO_DIA` é calculado para **qualquer** célula cujo dia seguinte tenha outra corrida
    //   — e a corrida de uma **paragem** é `-1`, logo uma viatura parada na véspera de um
    //   serviço recebe a bandeira. 📌 O painel dizia então **«partida: POR DEFINIR — último dia
    //   da viagem»** sobre um dia em que a viatura está estacionada e não há viagem nenhuma.
    //
    //   ⚠️ É a família «um valor plausível no lugar do certo»: não dá erro, não fica em branco,
    //   e afirma uma partida que não existe — que é pior do que não dizer nada, porque quem lê
    //   vai procurar a hora do voo.
    //
    // 📌 **Encontrado a auditar o `comparar.mjs` célula a célula**, e não por um sintoma: duas
    //   células divergiam só nesta bandeira, e a pergunta «o que é que ela faz aparecer?» levou
    //   aqui. O `estadoViatura < 0` já excluía oficina e avaria; faltava excluir **o nada**.
    (celula[10] >= 0 || celula[5] >= 0 || celula[0] >= 0)
  ) {
    // O último dia é o da partida do grupo, e a hora é a do voo. Se falta, diz-se.
    pares.push(['partida', 'POR DEFINIR — último dia da viagem']);
  }

  if ((bandeiras & BANDEIRA.ROTACAO_SUSPEITA) !== 0) {
    // Foi o cliente a apanhar: «tem um dia livre, não tem in nesse dia de nenhum
    // itinerário». Mede-se em 14,3 % dos dias que vinham marcados como rotação.
    // ⚠️⚠️ **A MESMA FRASE do cartão, e em portugues corrente.** Duas redaccoes para o mesmo
    //   facto ensinam que sao dois factos — e a antiga («marcado na origem … nao e rotacao»)
    //   eram tres termos internos e nenhuma instrucao.
    pares.push([
      'atenção (!)',
      'estava marcado como «chega e parte no mesmo dia», mas no dia seguinte a viatura ' +
        'não tem serviço — logo não parte. É só uma chegada. Confirme se falta lançar o ' +
        'serviço seguinte.',
    ]);
  }

  // ⚠️⚠️ **O LIMITE ENTRE VIAGENS SEM CAIXA PRETA — e a pergunta que o cliente fez.**
  //
  // 📗 Cliente, 2026-08-11: *«porque e que uma vez tem o dia de rotacao entre viagens e noutras
  //    nao?? no autocarro 143»*.
  //
  // 📌 **O comportamento estava certo e o mapa nao o explicava.** A rotacao exige as duas coisas
  // no MESMO dia — uma chegada e uma partida (D-080, e a regra e derivada em `mapa.ts`). Duas
  // viagens que se encontram em dias DIFERENTES nao produzem caixa preta nenhuma, e nao ha nada
  // errado nisso: a viatura acaba uma hoje e comeca outra amanha.
  //
  // ⚠️ **Medido na escala real da GVA, nos 1 577 limites entre viagens consecutivas da mesma
  // viatura:** so **20,9 %** tem a viagem seguinte a comecar no MESMO dia; **44,3 %** tem um dia de
  // intervalo e **34,8 %** mais do que um. 📌 Ou seja: em **4 de cada 5** limites nao ha rotacao
  // para marcar — o que o planeador viu como incoerencia e o caso normal.
  //
  // ⚠️⚠️ **Duas barras encostadas e duas barras com uma caixa preta pelo meio parecem o mesmo
  // gesto com resultados diferentes, e o ecra nao dizia porque.** E a familia «o sistema sabe e nao
  // diz»: nao se corrige o comportamento, corrige-se o silencio.
  if (
    (bandeiras & BANDEIRA.ROTACAO) === 0 &&
    (bandeiras & BANDEIRA.ROTACAO_SUSPEITA) === 0 &&
    (bandeiras & BANDEIRA.ULTIMO_DIA) !== 0 &&
    estadoViatura < 0
  ) {
    const seguinte = dados.grelha[viatura]?.[dia + 1];
    const comecaAmanha = seguinte != null && seguinte[10] >= 0 && seguinte[10] !== celula[10];
    if (comecaAmanha) {
      pares.push([
        'sem rotação',
        'a viagem seguinte começa no dia seguinte, e não neste — logo não há chegada e partida ' +
          'no mesmo dia, que é o que faz um dia de rotação. Na escala actual da GVA isto é o ' +
          'caso mais comum: só 1 em cada 5 limites entre viagens tem as duas no mesmo dia.',
      ]);
    }
  }

  // ---- ⚠️⚠️ AS DUAS VIAGENS DO DIA DE ROTAÇÃO ------------------------------
  //
  // 📗 Cliente, 2026-08-12: *«o dia de rotação tem de ser visto nos detalhes como o primeiro dia de
  //    uma viagem e o último dia da outra, com os botões correspondentes a esses dias»*.
  //
  // 📌 **Até aqui o painel descrevia UMA viagem.** A célula do dia de rotação é construída pela
  //    linha `principal`, que é a viagem que **ACABA** (medido: 1 029 de 1 029 têm a referência do
  //    dia anterior) — logo a viagem que **PARTE** existia nos campos 11–13 desde a migração 027 e
  //    **não aparecia em sítio nenhum**. Estrutura carregada e o elo a menos, outra vez.
  //
  // ⚠️ **E era isto que fazia a hora parecer errada:** o planeador via os detalhes de uma viagem e a
  //    hora da outra, sem nada a dizer que eram duas.
  //
  // ⚠️ A hora: no dia de rotação a semântica das sublinhas **inverte-se** (D-080) — a primeira é a
  //    CHEGADA da que acaba, a segunda é a PARTIDA da que começa. É o que os planeadores já fazem,
  //    medido em 126 células.
  // ✅ **A que grupo pertence cada hora JÁ NÃO ESTÁ EM ABERTO — D-084 fechou a 2026-08-14**, o
  //    cliente decidiu e a **migração 028** corrigiu o que a 027 tinha gravado ao contrário: o
  //    `out` é da viagem que **ACABA**, o `in` da que **PARTE**. ⚠️ Até 2026-09-05 este comentário
  //    ainda dizia «fica em aberto». Os 23 % de casos com a partida antes da chegada continuam a
  //    ser verdade — e agora são o **normal** e não uma ambiguidade: o grupo antigo voa às 14:25 e
  //    o novo aterra às 18:30.
  if (celula[13] >= 0) {
    const cliSaida = celula[11] >= 0 ? dados.clientes[celula[11]] : undefined;
    pares.push([
      '⤵ e HOJE PARTE outra viagem',
      `${dados.corridas[celula[13]] ?? ''}${
        cliSaida === undefined ? '' : ` · ${cliSaida[0]} — ${cliSaida[1]}`
      }${celula[12] >= 0 ? ` · ${itinerarioParaDetalhe(dados, celula[12])}` : ''}`,
    ]);
    // ---- ⚠️⚠️ O CONDUTOR DESSA VIAGEM (campo 14) -------------------------
    //
    //   📗 Cliente, 2026-08-14: *«muitas vezes é no dia de rotação que se muda o motorista»*.
    //   Medido na folha real: **50 de 293 rotações (17,1 %)** têm condutor diferente na
    //   véspera e no dia seguinte. ⚠️ E o campo 14 **não é o campo 6**: dois condutores em
    //   sequência não são tripulação dupla (art. 8.º n.º 5), e chamar-lhes o mesmo dava um
    //   regime de repouso errado.
    if (celula[14] >= 0) {
      pares.push([
        'motorista da viagem que PARTE',
        `${nomeDoMotorista(dados, celula[14])} — conduz a segunda metade do dia`,
      ]);
    } else {
      pares.push(['motorista da viagem que PARTE', 'por atribuir']);
    }
    // ⚠️ E a tripulação DUPLA dessa viagem, se houver (campo 15). Sem esta linha o segundo
    //   condutor dela existia nos dados e não aparecia em sítio nenhum.
    if (celula[15] >= 0) {
      pares.push([
        '2.º condutor da que PARTE',
        `${nomeDoMotorista(dados, celula[15])} — tripulação dupla nessa viagem (art. 8.º n.º 5)`,
      ]);
    }

    // ---- ⚠️⚠️ AS HORAS, CADA UMA LIDA DA LINHA DA SUA VIAGEM --------------
    //
    //   📗 Cliente, 2026-08-14: *«a hora de saída é a hora de saída de uma viagem e a hora de
    //   entrada é a hora da chegada da outra viagem»*. **Tem razão, e o painel dizia o
    //   contrário:** lia `movimento[1]` — a segunda hora do par do dia — e chamava-lhe «hora
    //   de partida dessa viagem». O par do dia não sabe de quem é cada hora; a **linha** sabe.
    //
    // 📌 Agora cada hora sai da linha a que está pendurada: `movimentos` é a linha que CHEGA,
    //   `movimentosSaida` é a que PARTE. Nenhuma convenção é aplicada — diz-se o que está
    //   registado, e é isso que torna um erro de atribuição **visível** em vez de plausível.
    //
    // ✅ **D-084 FECHOU a 2026-08-14, e este comentário dizia o contrário até 2026-09-05.** A
    //   medição que aqui estava era o argumento (`tools/escala-real/horas-e-condutor-na-rotacao.mjs`):
    //   os PRIMEIROS dias de serviço levam `in` **513** vezes contra **5** `out`, e os dias de
    //   rotação levam `in` **90** contra **3** — um dia de rotação comporta-se como o primeiro dia
    //   da viagem que parte, logo o `out` é da viagem que **ACABA**. O cliente decidiu nesse
    //   sentido e a **migração 028** trocou o que a 027 tinha gravado ao contrário.
    // 📌 **O que este bloco faz continua a ser o mesmo, e é isso que importa:** cada hora sai da
    //   **sua linha**, sem convenção aplicada. É o que torna um erro de atribuição visível.
    //
    // ⚠️⚠️ **E ATÉ 2026-09-07 A LINHA DA QUE CHEGA ERA O PAR DO DIA — logo as duas viagens
    //   mostravam as MESMAS horas.** O par do dia é a fusão das duas linhas (a `partida`
    //   escreve por cima, de propósito: seis leitores mostram «as horas do dia»), e quando a
    //   `principal` não tem movimento nenhum o par fica a ser, byte a byte, o da `partida`.
    //   Medido no `dev` — viatura **297**, **2026-08-29**, plano `P-ACO`: `partida`
    //   (`S8387-75A`) com `in 08:55` e `out 19:40`, `principal` (`MPT-P006323`) **sem nenhum**,
    //   e o ecrã a escrever «chegada 08:55 · saída 19:40» nas duas.
    //
    // 📌 **Isto é pior do que um campo vazio a fingir que informa** — a família já registada:
    //   não estava vazio, estava preenchido com o valor da OUTRA viagem. Por isso a metade que
    //   protege é a segunda, e não a primeira: cada rótulo lê a **sua** linha, e a viagem que
    //   não tem hora **diz que não tem**.
    const movSaida = dados.movimentosSaida[`${viatura}:${dia}`] ?? null;
    const horasDaPartida = descreverHoras(movSaida);
    pares.push([
      'hora registada na viagem que PARTE',
      horasDaPartida !== '' ? horasDaPartida : 'POR DEFINIR — nada registado nesta viagem',
    ]);
    const movChegada = dados.movimentosChegada[`${viatura}:${dia}`] ?? null;
    const horasDaChegada = descreverHoras(movChegada);
    pares.push([
      'hora registada na viagem que CHEGA',
      horasDaChegada !== '' ? horasDaChegada : 'POR DEFINIR — nada registado nesta viagem',
    ]);
  }

  if ((bandeiras & BANDEIRA.ROTACAO) !== 0) {
    const minutos = margemDaRotacaoDoDia(dados, viatura, dia);
    // ---- ⚠️⚠️ O RÓTULO DIZ QUAL DOS DOIS É, e até 2026-09-07 dizia «de rotação» aos dois ----
    //
    //   O número sozinho não se lê. Com o sinal invertido, o caso mau saía positivo e grande —
    //   **«10h45 de rotação»** sobre 10h45 de ESPERA do grupo (viatura 297, `dev`, 2026-08-29).
    //   Corrigir o sinal sem corrigir a palavra trocava um número enganador por outro: «−10h45
    //   de rotação» continua a não dizer quem espera.
    //
    // 📌 Os dois casos do cliente, na sua linguagem: `out` 12 e `in` 18 é **folga** e não tem
    //   problema; `in` 12 e `out` 18 é **espera** e tem. Ver `tempoRotacaoMinutos`.
    if (minutos === null) {
      pares.push(['dia de rotação', 'sim — tempo não calculável']);
    } else if (minutos < 0) {
      // ⚠️ ESTE É O CASO MAU, e é aqui que o planeador precisa da palavra e não do número: o
      //   grupo novo aterra e o autocarro ainda anda com a viagem anterior.
      //
      // ⚠️ **Sem «abaixo do mínimo configurado».** O `rotacao_minima_h` (D-073) é a folga
      //   mínima, e aqui não há folga nenhuma — acrescentar um aviso que dispara em 100 % deste
      //   ramo diluía a única frase que importa. Quem tem limite próprio para esta metade é o
      //   motor (`rotacao_espera_max_h`, omissão 4 h, que **recusa** a mobilização); no painel
      //   isto é apresentação, e o painel não conhece esse parâmetro.
      pares.push([
        'dia de rotação',
        `${duracao(-minutos)} de ESPERA do grupo novo — o autocarro ainda está com a viagem que acaba`,
      ]);
    } else {
      // A folga do autocarro: o grupo que acaba já saiu quando o novo chega. É o caso saudável,
      // e inclui os **23 %** de caixas pretas reais com a partida antes da chegada (viatura 216:
      // `out 13:10` com `in 18:30` = 5h20 de folga). Marcar isso como suspeito era erro nosso.
      pares.push([
        'dia de rotação',
        `${duracao(minutos)} de folga entre as duas viagens` +
          (minutos < minutosRotacaoMinimo ? ' — abaixo do mínimo configurado' : ''),
      ]);
    }
  }

  if (meio >= 0) pares.push(['meio', dados.meios[meio] ?? '']);
  pares.push([
    'registo',
    (bandeiras & BANDEIRA.RESERVA) !== 0 ? 'RESERVA — não confirmado' : 'definitivo',
  ]);
  if ((bandeiras & BANDEIRA.DUVIDA) !== 0) pares.push(['marcado', 'provisório']);
  // ⚠️ 📗 «um pisco que diga: ok, aqui já não quero mexer mais». **A consequência vai na frase** e
  //    não só a marca: um alfinete sem dizer o que faz é decoração, e este faz o motor ignorar o
  //    serviço no planeamento e nos rearranjos.
  if ((bandeiras & BANDEIRA.FIXADO) !== 0) {
    pares.push(['fixado', 'não entra no planeamento nem nos rearranjos automáticos']);
  }
  pares.push(['nível', dados.estados[estado] ?? '']);

  return pares;
}

/**
 * O cartão junto ao cursor, com a marca de quem mexeu na célula.
 *
 * ⚠️ **A marca vai no FIM**, ao contrário da nota: a nota é a razão provável de se estar a
 * olhar para a célula, esta é contexto sobre o registo. Num cartão que se lê em passagem, o
 * conteúdo vem primeiro.
 *
 * ⚠️⚠️ **CUSTO EM NÓS: zero acrescentados ao mapa.** O cartão é UM elemento, desenhado uma vez
 * para a célula sob o cursor — não é por célula. A linha nova é um `<li>` a mais **nesse**
 * cartão, e só quando a célula tem carimbo; o mapa em si (3 889 nós) não ganha nenhum.
 */
export function resumo(
  dados: DadosMapa,
  viatura: number,
  dia: number,
  agora: number = Date.now(),
): readonly string[] {
  const linhas = resumoDaCelula(dados, viatura, dia);
  const marca = fraseDaAlteracao(dados.alteracoes[`${viatura}:${dia}`], agora);
  return marca === null ? linhas : [...linhas, marca];
}

/**
 * O painel do dia, com a marca de quem mexeu na célula.
 *
 * ⚠️ **Par próprio (`alterado`) e não uma frase no meio de outra**: o painel é uma lista de
 * definições, e quem procura «isto é recente?» procura um rótulo, não uma frase.
 */
export function detalhe(
  dados: DadosMapa,
  viatura: number,
  dia: number,
  minutosRotacaoMinimo: number,
  agora: number = Date.now(),
): readonly (readonly [string, string])[] {
  const pares = detalheDaCelula(dados, viatura, dia, minutosRotacaoMinimo);
  const marca = fraseDaAlteracao(dados.alteracoes[`${viatura}:${dia}`], agora);
  return marca === null ? pares : [...pares, ['alterado', marca] as const];
}
